import { forwardRef, useCallback, useMemo } from 'react';

import {
  Flex,
  type FlexProps,
  Text,
  AssistanceIcon,
  AssistanceRejectedIcon,
} from '@frontend/legacy-components';
import { toCamelCase } from '@frontend/utils';

import type { FieldSuggestionProvenance } from '../InputGroupWithSuggestions';

const PROVENANCE_TEXT: Record<string, string> = {
  null: '',
  filename: 'From file name ',
  content: 'Found in ',
  drawingNumber: 'From drawing number ',
  cadAttribute: 'Found in ',
};

const ProvenanceText = ({
  provenanceByline,
}: {
  provenanceByline: [string, string, string?];
}) => (
  <Text size="sm" color="feature.secondary">
    {provenanceByline[0] + ' '}
    <Text
      as="span"
      size="sm"
      color="interaction.suggestion"
      wordBreak="break-all"
      whiteSpace="pre-line"
      m={0}
    >
      {provenanceByline[1].trim()}
    </Text>
    {provenanceByline[2] ? ' ' + provenanceByline[2] : ''}
  </Text>
);
ProvenanceText.displayName = 'ProvenanceText';

interface SuggestionMenuItemProps extends Omit<FlexProps, 'onSelect'> {
  value: string;
  provenance?: FieldSuggestionProvenance[];
  isRejected?: boolean;
  index: number;
  isFocused?: boolean;
  /** Value passed to onSelect; defaults to `value` when omitted. */
  selectValue?: string;
  onSelect?: (value: string) => void;
  onHover?: (index: number) => void;
}

export const SuggestionMenuItem = forwardRef<
  HTMLDivElement,
  SuggestionMenuItemProps
>(
  (
    {
      value,
      provenance,
      isRejected,
      index,
      isFocused,
      selectValue,
      onSelect,
      onHover,
      ...props
    },
    ref,
  ) => {
    const handleClick = useCallback(() => {
      onSelect?.(selectValue ?? value);
    }, [onSelect, selectValue, value]);

    const handleMouseEnter = useCallback(() => {
      onHover?.(index);
    }, [onHover, index]);

    const provenanceByline: [string, string, string?] | null = useMemo(() => {
      if (!provenance || !provenance.length) return null;
      if (provenance.length === 1) {
        return [
          PROVENANCE_TEXT[toCamelCase(provenance[0].metadata?.src ?? 'null')],
          provenance[0].filename,
        ];
      }
      const moreFiles = provenance.length - 1;
      return [
        'From',
        provenance[0].filename,
        `and ${moreFiles} more file${moreFiles > 1 ? 's' : ''}`,
      ];
    }, [provenance]);

    return (
      <Flex
        ref={ref}
        cursor="pointer"
        py={0.5}
        pr={1}
        pl={2}
        h="auto"
        w="100%"
        justify="space-between"
        align="start"
        bg={isFocused ? 'interaction.surfaceSuggestionHover' : undefined}
        _active={{
          bg: 'interaction.surfaceSuggestionActive',
        }}
        {...props}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
      >
        <Flex direction="column" align="flex-start" mr={1}>
          <Text
            wordBreak="break-all"
            whiteSpace="pre-line"
            data-fs-hide="true"
            m={0}
          >
            {value.trim()}
          </Text>
          {!isRejected && provenanceByline && (
            <ProvenanceText provenanceByline={provenanceByline} />
          )}
        </Flex>
        {isRejected ? (
          <AssistanceRejectedIcon boxSize={3} color="feature.placeholder" />
        ) : (
          <AssistanceIcon boxSize="1.375rem" color="interaction.suggestion" />
        )}
      </Flex>
    );
  },
);
SuggestionMenuItem.displayName = 'SuggestionMenuItem';
